import React, { useId } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import { Box, Avatar, Typography } from "@mui/material";
import { Verified } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import utils from "../../universal/utils";
import { pickAvatarUpdatedAtMs, resolveAvatarSrc } from "../../utils/avatarUrl";
import TooltipComponent from "../common/Tooltip";
import EloBadge from "../common/EloBadge.jsx";
import { toProfilePath } from "../../universal/v2Routes";

const basePath = utils.getEdgeBasePath();

const rowSx = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflow: "hidden",
    minHeight: "3.25rem",
    gap: { xs: "0.42rem", xl: "0.46rem" },
    padding: { xs: "0.28rem 0.4rem", xl: "0.3rem 0.44rem" },
    borderRadius: "10px",
    background:
            "linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.015) 100%)",
    border: `1px solid transparent`,
    boxShadow: "0 4px 18px rgba(0,0,0,0.28), 0 0 0 1px rgba(255,190,90,0.08)",
    transition:
        "background-color 180ms ease, border-color 180ms ease, transform 180ms ease, box-shadow 180ms ease",
    cursor: "pointer",
    textAlign: "left",
    "&:hover": {
        background:
            "linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.022) 100%)",
        border: `1px solid ${alpha(colors.primary.main, 0.65)}`,
        transform: "translateY(-2px)",
    },
    "&:focus-visible": {
        outline: `2px solid ${alpha(colors.primary.main, 0.42)}`,
        outlineOffset: 3,
    },
};

function OuroVerifiedBadge({ gradientId }) {
    return (
        <TooltipComponent title="Ouro verified" placement="top">
            <Box
                component="span"
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    lineHeight: 0,
                    position: "relative",
                    filter: `
                        drop-shadow(0 1px 0 rgba(255,255,255,0.18))
                        drop-shadow(0 3px 10px rgba(246,185,76,0.22))
                    `,
                    "& svg": {
                        fontSize: { xs: "1.1rem", xl: "1.2rem" },
                        fill: `url(#${gradientId})`,
                    },
                }}
            >
                <svg width="0" height="0" aria-hidden="true" style={{ position: "absolute" }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#FFF4CC" />
                            <stop offset="18%" stopColor="#F6D67A" />
                            <stop offset="48%" stopColor="#F6B94C" />
                            <stop offset="100%" stopColor="#C97A16" />
                        </linearGradient>
                    </defs>
                </svg>
                <Verified />
            </Box>
        </TooltipComponent>
    );
}

OuroVerifiedBadge.propTypes = {
    gradientId: PropTypes.string.isRequired,
};

function sameStringValue(a, b) {
    return a != null && b != null && String(a) === String(b);
}

export default function ProfileFriendRow({
    friend,
    viewerUser,
}) {
    const history = useHistory();
    const location = useLocation();
    const gradientId = useId().replace(/:/g, "");
    const friendData = friend?.friendData;

    if (!friendData) {
        return null;
    }

    const avatarSrc = resolveAvatarSrc({
        basePath,
        picture: friendData?.picture,
        userId: friendData?.userId || friendData?._id,
        avatarUpdatedAtMs: pickAvatarUpdatedAtMs(friendData),
        hasCustomAvatar: !!friendData?.avatar,
    });
    const isOuroVerified =
        (friendData?.accountStatus || "").toString().trim().toLowerCase() === "verified";
    const profileUserId = friendData?._id || friendData?.userId;
    const friendSteamId =
        friend?.friendSteamId || friendData?.steamId || friendData?.steamId64 || friendData?.steam64Id;
    const viewerSteamId =
        viewerUser?.steamId || viewerUser?.steamId64 || viewerUser?.steam64Id;
    const isViewerRow =
        sameStringValue(profileUserId, viewerUser?._id) ||
        sameStringValue(profileUserId, viewerUser?.userId) ||
        sameStringValue(friendSteamId, viewerSteamId);

    const handleProfile = () => {
        const profilePath = toProfilePath(profileUserId, location.pathname);
        if (profilePath) {
            history.push(profilePath);
        }
    };

    const handleRowKeyDown = (event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleProfile();
        }
    };

    return (
        <Box
            role="button"
            tabIndex={profileUserId ? 0 : -1}
            onClick={handleProfile}
            onKeyDown={handleRowKeyDown}
            sx={rowSx}
        >
            <Box
                sx={{
                    position: "relative",
                    flexShrink: 0,
                    width: { xs: "3rem", xl: "3.2rem" },
                    height: { xs: "3rem", xl: "3.2rem" },
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `
                        radial-gradient(
                            circle at top,
                            rgba(246,185,76,0.16),
                            rgba(255,255,255,0.03)
                        )
                    `,
                    transition: "all 260ms ease",
                    border: "1px solid rgba(255,255,255,0.08)",
                    boxShadow: `
                        inset 0 1px 0 rgba(255,255,255,0.06),
                        0 6px 16px rgba(0,0,0,0.28)
                    `,
                }}
            >
                <Avatar
                    src={avatarSrc}
                    alt={friendData.nickname}
                    sx={{
                        position: "relative",
                        width: "90%",
                        height: "90%",
                        border: "2px solid rgba(255,255,255,0.08)",
                        boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                    }}
                />
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.35rem",
                    minWidth: 0,
                    flex: 1,
                    overflow: "hidden",
                }}
            >
                <Typography
                    component="span"
                    title={friendData.nickname}
                    sx={{
                        color: colors.text.primary,
                        fontSize: { xs: "0.95rem", xl: "1rem" },
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                        lineHeight: 1.25,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                        flex: "0 1 auto",
                    }}
                >
                    {friendData.nickname}
                    {isViewerRow ? (
                        <Box
                            component="span"
                            sx={{
                                fontSize: "0.85em",
                                fontWeight: 500,
                                ml: 0.35,
                            }}
                        >
                            (you)
                        </Box>
                    ) : null}
                </Typography>
                {isOuroVerified ? (
                    <OuroVerifiedBadge gradientId={gradientId} />
                ) : null}
            </Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    flexShrink: 0,
                    ml: "auto",
                }}
            >
                <EloBadge value={friendData.elo} size="small" />
            </Box>
        </Box>
    );
}

ProfileFriendRow.propTypes = {
    friend: PropTypes.object.isRequired,
    viewerUser: PropTypes.object,
};
